import React, { useState, useEffect } from 'react';
import { Building2, Phone, MessageSquare, Wifi, Star, AlertTriangle, CheckCircle } from 'lucide-react';
import { operatorAPI, dashboardAPI } from '../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

// Import operator logos
import unitelLogo from '../assets/logos/unitellogo.png';
import ltcLogo from '../assets/logos/ltclogo.png';
import tplusLogo from '../assets/logos/tpluslogo.png';
import etlLogo from '../assets/logos/etllogo.png';
import bestLogo from '../assets/logos/bestlogo.png';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ByOperatorView = ({ month, year }) => {
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [operatorsData, setOperatorsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const monthParam = month != null ? month : new Date().getMonth() + 1;
  const yearParam = year != null ? year : new Date().getFullYear();

  // Operator logos mapping
  const operatorLogos = {
    UNITEL: unitelLogo,
    LTC: ltcLogo,
    TPLUS: tplusLogo,
    ETL: etlLogo,
    BEST: bestLogo
  };

  // Operator colors mapping  
  const operatorColors = {
    UNITEL: '#3b82f6',
    LTC: '#10b981', 
    TPLUS: '#8b5cf6',
    ETL: '#f59e0b',
    BEST: '#ef4444'
  };

  // Fetch operator data from database (refetch when month or year changes)
  useEffect(() => {
    fetchOperatorData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when period changes
  }, [monthParam, yearParam]);

  const fetchOperatorData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all operators
      const operatorsResponse = await operatorAPI.getAll();
      console.log('Operators response:', operatorsResponse);
      
      if (!operatorsResponse || !operatorsResponse.data) {
        throw new Error('No operator data received');
      }

      const operators = operatorsResponse.data;
      const processedOperators = {};

      // Process each operator
      for (const operator of operators) {
        const operatorName = operator.operator_name || operator.name;
        const operatorCode = operator.operator_code || operator.code;
        
        // Get detailed operator data (pass month/year for filtered data)
        const operatorDetails = await dashboardAPI.getOperator(operator.operator_id || operator.id, {
          month: monthParam,
          year: yearParam
        });

        console.log(`Processing operator ${operatorName}:`, { operatorDetails });

        // Process the operator data
        processedOperators[operatorCode] = processOperatorData(
          operator, 
          operatorDetails
        );
      }

      setOperatorsData(processedOperators);
      
      // Set default selected operator to the first one
      const firstOperatorCode = Object.keys(processedOperators)[0];
      if (firstOperatorCode && !selectedOperator) {
        setSelectedOperator(firstOperatorCode);
      }
      
    } catch (err) {
      console.error('Error fetching operator data:', err);
      setError('Failed to load operator data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const processOperatorData = (operator, operatorDetails) => {
    const operatorName = operator.operator_name || operator.name;
    const operatorCode = operator.operator_code || operator.code;
    
    // Default values for missing data
    const defaultService = {
      subscribers: 0,
      revenue: 0,
      usage: null,
      compliance: 95.0,
      marketShare: 0,
      growth: 0
    };

    // Process services from operator details
    const services = {
      voice: { ...defaultService },
      sms: { ...defaultService },
      data: { ...defaultService },
      vas: { ...defaultService }
    };

    // Process operator details if available
    if (operatorDetails && operatorDetails.data && operatorDetails.data.services) {
      const operatorServicesData = operatorDetails.data.services;
      
      Object.keys(services).forEach(serviceKey => {
        if (operatorServicesData[serviceKey]) {
          const serviceData = operatorServicesData[serviceKey];
          services[serviceKey] = {
            subscribers: parseInt(serviceData.total_subscribers || 0),
            revenue: parseFloat(serviceData.total_revenue || 0),
            usage: parseFloat(serviceData.average_usage || null),
            compliance: parseFloat(serviceData.compliance_rate || 95.0),
            marketShare: parseFloat(serviceData.market_share || 0),
            growth: parseFloat(serviceData.growth_rate || 0)
          };
        }
      });
    }

    // Calculate overall compliance from services
    const complianceValues = Object.values(services)
      .filter(s => s.compliance !== undefined)
      .map(s => parseFloat(s.compliance || 95));
    const overallCompliance = complianceValues.length > 0 
      ? complianceValues.reduce((sum, val) => sum + val, 0) / complianceValues.length 
      : 95.0;

    // Calculate total subscribers and revenue
    const totalSubscribers = Object.values(services)
      .filter(s => s.subscribers !== undefined)
      .reduce((sum, s) => sum + (parseInt(s.subscribers) || 0), 0);
    
    const totalRevenue = Object.values(services)
      .filter(s => s.revenue !== undefined)
      .reduce((sum, s) => sum + (parseFloat(s.revenue) || 0), 0);

    // Generate monthly trend (mock data based on total revenue)
    const baseRevenue = totalRevenue / 1000000000; // Convert to billions
    const monthlyTrend = Array.from({ length: 12 }, (_, i) => {
      const variation = (Math.sin(i * Math.PI / 6) * 0.1 + Math.random() * 0.05 - 0.025) * baseRevenue;
      return Math.max(0, baseRevenue + variation);
    });

    // Generate issues and strengths based on compliance
    const issues = [];
    const strengths = [];
    
    Object.entries(services).forEach(([key, service]) => {
      if (service.compliance < 90) {
        issues.push(`${key.toUpperCase()} compliance critical (${service.compliance.toFixed(1)}%)`);
      } else if (service.compliance < 95) {
        issues.push(`${key.toUpperCase()} compliance needs improvement`);
      }
      
      if (service.growth && service.growth > 15) {
        strengths.push(`Strong ${key} growth (${service.growth.toFixed(1)}%)`);
      }
    });

    if (overallCompliance >= 98) {
      strengths.push('Excellent overall compliance');
    }
    
    if (totalSubscribers > 5000000) {
      strengths.push('Large subscriber base');
    }

    return {
      name: operatorName,
      logo: operatorLogos[operatorCode] || operatorLogos[operatorName] || '/default-logo.png',
      color: operatorColors[operatorCode] || operatorColors[operatorName] || '#6b7280',
      established: operator.established_year || '2000',
      marketPosition: getMarketPosition(totalSubscribers),
      overallCompliance: Math.round(overallCompliance * 10) / 10,
      totalSubscribers: totalSubscribers,
      totalRevenue: totalRevenue,
      services: services,
      monthlyTrend: monthlyTrend,
      issues: issues.length > 0 ? issues : ['No major issues identified'],
      strengths: strengths.length > 0 ? strengths : ['Stable operations']
    };
  };

  const getMarketPosition = (subscribers) => {
    if (subscribers > 8000000) return 'Market Leader';
    if (subscribers > 5000000) return 'Major Player';
    if (subscribers > 3000000) return 'Established Player';
    if (subscribers > 1000000) return 'Growing Competitor';
    return 'New Entrant';
  };

  const formatKip = (amount) => {
    if (amount >= 1000000000) {
      return `${(amount / 1000000000).toFixed(1)}B ₭`;
    } else if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M ₭`;
    }
    return new Intl.NumberFormat('lo-LA').format(amount) + ' ₭';
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('lo-LA').format(num);
  };

  const getComplianceStatus = (rate) => {
    if (rate >= 98) return { status: 'excellent', color: 'green', text: 'Excellent', bgColor: 'bg-green-100', textColor: 'text-green-800' };
    if (rate >= 95) return { status: 'good', color: 'blue', text: 'Good', bgColor: 'bg-blue-100', textColor: 'text-blue-800' };
    if (rate >= 90) return { status: 'warning', color: 'yellow', text: 'Needs Review', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' };
    return { status: 'critical', color: 'red', text: 'Critical', bgColor: 'bg-red-100', textColor: 'text-red-800' };
  };

  // Add loading state
  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          </div>
          <p className="text-center text-gray-600 mt-4">Loading operator data...</p>
        </div>
      </div>
    );
  }

  // Add error state
  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">Error Loading Data</h3>
                <p className="text-red-600 mt-1">{error}</p>
                <button 
                  onClick={fetchOperatorData}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if we have data and selected operator
  if (Object.keys(operatorsData).length === 0) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-gray-600">No operator data available.</p>
          </div>
        </div>
      </div>
    );
  }

  const selectedOperatorData = operatorsData[selectedOperator];

  if (!selectedOperatorData) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-gray-600">Please select an operator to view details.</p>
          </div>
        </div>
      </div>
    );
  }

  // Create service breakdown chart
  const serviceBreakdownData = {
    labels: ['Voice', 'SMS', 'Data', 'VAS'],
    datasets: [{
      data: [
        selectedOperatorData.services.voice.revenue,
        selectedOperatorData.services.sms.revenue,
        selectedOperatorData.services.data.revenue,
        selectedOperatorData.services.vas.revenue
      ],
      backgroundColor: ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b'],
      borderWidth: 2,
      borderColor: '#ffffff'
    }]
  };

  // Revenue trend chart
  const revenueTrendData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [{
      label: 'Monthly Revenue',
      data: selectedOperatorData.monthlyTrend,
      borderColor: selectedOperatorData.color,
      backgroundColor: selectedOperatorData.color + '20',
      fill: true,
      tension: 0.4
    }]
  };

  // Market share comparison
  const marketShareData = {
    labels: ['Voice', 'SMS', 'Data', 'VAS'],
    datasets: [{
      label: selectedOperator,
      data: [
        selectedOperatorData.services.voice.marketShare,
        selectedOperatorData.services.sms.marketShare,
        selectedOperatorData.services.data.marketShare,
        selectedOperatorData.services.vas.marketShare
      ],
      backgroundColor: selectedOperatorData.color,
      borderColor: selectedOperatorData.color,
      borderWidth: 1
    }]
  };

  const serviceIcons = {
    voice: Phone,
    sms: MessageSquare,
    data: Wifi,
    vas: Star
  };

  return (
    <div className="space-y-6">
      {/* Operator Selection */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-4">
          <Building2 className="h-6 w-6 text-blue-600 mr-3" />
          <h2 className="text-xl font-bold text-gray-900">Regulatory View: By Operator</h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(operatorsData).map(([key, operator]) => (
            <button
              key={key}
              onClick={() => setSelectedOperator(key)}
              className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                selectedOperator === key 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex flex-col items-center space-y-3">
                <img 
                  src={operator.logo} 
                  alt={operator.name}
                  className="w-12 h-12 object-contain"
                />
                <div className="text-center">
                  <div className="font-semibold text-gray-900">{operator.name}</div>
                  <div className="text-xs text-gray-500">{operator.marketPosition}</div>
                  <div className={`text-xs font-medium mt-1 ${getComplianceStatus(operator.overallCompliance).textColor}`}>
                    {operator.overallCompliance}% Compliance
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>


      {/* Selected Operator Overview */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <img 
              src={selectedOperatorData.logo} 
              alt={selectedOperatorData.name}
              className="w-16 h-16 bg-white p-2 rounded-lg object-contain"
            />
            <div>
              <h2 className="text-2xl font-bold">{selectedOperatorData.name}</h2>
              <p className="text-blue-100">Established: {selectedOperatorData.established}</p>
              <p className="text-blue-100">{selectedOperatorData.marketPosition}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{selectedOperatorData.overallCompliance}%</div>
            <div className="text-blue-100">Overall Compliance</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white bg-opacity-20 rounded-lg p-4">
            <div className="text-2xl font-bold">{formatNumber(selectedOperatorData.totalSubscribers)}</div>
            <div className="text-blue-100">Total Subscribers</div>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-4">
            <div className="text-2xl font-bold">{formatKip(selectedOperatorData.totalRevenue)}</div>
            <div className="text-blue-100">Monthly Revenue</div>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-4">
            <div className="text-2xl font-bold">4</div>
            <div className="text-blue-100">Service Types</div>
          </div>
        </div>
      </div>

      {/* Service Performance Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {Object.entries(selectedOperatorData.services).map(([serviceKey, service]) => {
          const ServiceIcon = serviceIcons[serviceKey];
          const compliance = getComplianceStatus(service.compliance);
          const serviceName = {
            voice: 'Voice',
            sms: 'SMS',
            data: 'Data',
            vas: 'VAS'
          }[serviceKey];

          return (
            <div key={serviceKey} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <ServiceIcon className="h-6 w-6 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">{serviceName}</h3>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${compliance.bgColor} ${compliance.textColor}`}>
                  {compliance.text}
                </span>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatNumber(service.subscribers)}
                  </div>
                  <div className="text-sm text-gray-600">Subscribers</div>
                  <div className="text-xs text-blue-600 font-medium">
                    {service.marketShare}% market share
                  </div>
                </div>

                <div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatKip(service.revenue)}
                  </div>
                  <div className="text-sm text-gray-600">Monthly Revenue</div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Compliance</span>
                  <span className="font-semibold">{service.compliance}%</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Growth</span>
                  <span className={`font-semibold ${service.growth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {service.growth > 0 ? '+' : ''}{service.growth}%
                  </span>
                </div>

                {service.usage && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Avg Usage</span>
                    <span className="font-semibold">
                      {service.usage}{serviceKey === 'voice' ? ' min' : serviceKey === 'data' ? ' GB' : ' SMS'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Service Revenue Breakdown */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue by Service</h3>
          <div className="h-64">
            <Doughnut 
              data={serviceBreakdownData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: { padding: 15, usePointStyle: true }
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${context.label}: ${formatKip(value)} (${percentage}%)`;
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Revenue Trend */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue Trend (12 months)</h3>
          <div className="h-64">
            <Line 
              data={revenueTrendData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(value) {
                        return formatKip(value * 1000000000);
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Market Share by Service */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Market Share by Service</h3>
          <div className="h-64">
            <Bar 
              data={marketShareData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    max: 50,
                    ticks: {
                      callback: function(value) {
                        return value + '%';
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Issues and Strengths */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Issues */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-6 w-6 text-orange-500 mr-3" />
            <h3 className="text-lg font-bold text-gray-900">Areas Requiring Attention</h3>
          </div>
          <ul className="space-y-2">
            {selectedOperatorData.issues.map((issue, index) => (
              <li key={index} className="flex items-start">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span className="text-sm text-gray-700">{issue}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Strengths */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
            <h3 className="text-lg font-bold text-gray-900">Key Strengths</h3>
          </div>
          <ul className="space-y-2">
            {selectedOperatorData.strengths.map((strength, index) => (
              <li key={index} className="flex items-start">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <span className="text-sm text-gray-700">{strength}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Regulatory Summary */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Regulatory Summary for {selectedOperator}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className={`text-2xl font-bold ${getComplianceStatus(selectedOperatorData.overallCompliance).textColor}`}>
              {selectedOperatorData.overallCompliance}%
            </div>
            <div className="text-sm text-gray-600">Overall Compliance</div>
            <div className={`text-xs mt-1 px-2 py-1 rounded ${getComplianceStatus(selectedOperatorData.overallCompliance).bgColor} ${getComplianceStatus(selectedOperatorData.overallCompliance).textColor}`}>
              {getComplianceStatus(selectedOperatorData.overallCompliance).text}
            </div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {Object.values(selectedOperatorData.services).filter(s => s.compliance >= 95).length}/4
            </div>
            <div className="text-sm text-gray-600">Services Above 95%</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {Object.values(selectedOperatorData.services).filter(s => s.compliance < 95 && s.compliance >= 90).length}
            </div>
            <div className="text-sm text-gray-600">Services Need Review</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {Object.values(selectedOperatorData.services).filter(s => s.compliance < 90).length}
            </div>
            <div className="text-sm text-gray-600">Critical Services</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ByOperatorView;